import { Global } from '@emotion/react';
import { useMount } from 'ahooks';
import { ConfigProvider } from 'antd';
import useSystem, { ColorMode, SystemSetting } from 'hooks/business/system/useSystem';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import { isQuickChat } from 'productLine';
import React, { useContext, useMemo, useCallback, useEffect, useState } from 'react';
import { isMlj } from 'services/chattt';
import createNamedContext from 'utils/createNamedContext';
import { createThemeVariableStyles, mljColors, themesColors } from './themeVariables';

export { themesColors } from './themeVariables';

// 暗色模式入口开关：
// - true：显示 AppHeader 的亮暗切换入口，resolvedMode 跟随 colorMode / 系统偏好
// - false：隐藏入口并强制 light
export const DARK_MODE_ENABLED = true;

export type Themes = SystemSetting['theme'];
const defaultTheme: Themes = 'Orange';
const defaultColorMode: ColorMode = 'system';
const ThemesContext = createNamedContext<{
  theme: Themes;
  colorMode: ColorMode;
  resolvedMode: Exclude<ColorMode, 'system'>;
  onChange: (theme: Themes) => void;
  onColorModeChange: (colorMode: ColorMode) => void;
}>('fett-theme', {
  theme: defaultTheme,
  colorMode: defaultColorMode,
  resolvedMode: 'light',
  onChange: () => {},
  onColorModeChange: () => {},
});

export const useThemeContext = () => useContext(ThemesContext);

export const ThemesProvider = ({ children }: { children: React.ReactNode }) => {
  const {
    systemSetting: { theme: settingTheme, colorMode },
    handleThemeSettingChange,
    handleColorModeSettingChange,
  } = useSystem();
  const { whiteListConfig: { AE_CONVERSATION = false } = {} } = useOrgConfigStateValue();
  const theme = isQuickChat || AE_CONVERSATION ? 'Purple' : settingTheme;
  const [systemResolvedMode, setSystemResolvedMode] = useState<'light' | 'dark'>('light');
  const resolvedMode = DARK_MODE_ENABLED
    ? colorMode === 'system'
      ? systemResolvedMode
      : colorMode
    : 'light';
  const updateTheme = useCallback(
    (theme: Themes) => {
      !AE_CONVERSATION && handleThemeSettingChange(theme);
    },
    [handleThemeSettingChange, AE_CONVERSATION],
  );
  const updateColorMode = useCallback(
    (nextColorMode: ColorMode) => {
      handleColorModeSettingChange(nextColorMode);
    },
    [handleColorModeSettingChange],
  );
  const CssVarRoot = useMemo(
    () => createThemeVariableStyles(theme, resolvedMode, isMlj),
    [resolvedMode, theme],
  );
  useMount(() => {
    updateTheme(theme);
  });
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const syncSystemMode = () => {
      setSystemResolvedMode(media.matches ? 'dark' : 'light');
    };
    syncSystemMode();
    if (media.addEventListener) {
      media.addEventListener('change', syncSystemMode);
      return () => {
        media.removeEventListener('change', syncSystemMode);
      };
    }
    media.addListener(syncSystemMode);
    return () => {
      media.removeListener(syncSystemMode);
    };
  }, []);
  useEffect(() => {
    ConfigProvider.config({
      theme: isMlj ? mljColors : themesColors[theme],
    });
  }, [theme]);
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.brand = theme.toLowerCase();
    root.dataset.colorMode = resolvedMode;
    root.dataset.colorModePreference = colorMode;
  }, [colorMode, resolvedMode, theme]);
  useEffect(() => {
    if (typeof window === 'undefined' || import.meta.env.VITE_ENV === 'production') {
      return;
    }

    window.__FETT_COLOR_LAB__ = {
      setColorMode: (nextColorMode: ColorMode) => {
        updateColorMode(nextColorMode);
        return {
          requestedMode: nextColorMode,
          colorMode,
          resolvedMode,
        };
      },
      getState: () => ({
        colorMode,
        resolvedMode,
        theme,
      }),
    };

    return () => {
      delete window.__FETT_COLOR_LAB__;
    };
  }, [colorMode, resolvedMode, theme, updateColorMode]);
  return (
    <ThemesContext.Provider
      value={{
        theme,
        colorMode,
        resolvedMode,
        onChange: updateTheme,
        onColorModeChange: updateColorMode,
      }}
    >
      <>
        <Global styles={CssVarRoot} />
        {children}
      </>
    </ThemesContext.Provider>
  );
};
export default ThemesProvider;
